import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { FaFacebookF, FaLinkedinIn, FaMediumM, FaWhatsapp, FaSearch, FaMapMarkerAlt, FaBriefcase, FaArrowRight } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import bgImage from "../assets/images/asdflobg.jpg";
import PlatformFeatures from "../components/PlatformFeatures";
import SignupModal from "../components/SignupModal";
import useAuthUser from "../hooks/useAuthUser";
import { auth } from "../firebase";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReferralModal from "../components/ReferralModal";
import BlogSection from "../components/BlogSection";

// SEO Meta tags for the landing page
const seoKeywords = [
  'autism jobs', 'special education jobs', 'free M-CHAT test', 'autism assessment',
  'autism screening', 'early intervention', 'autism therapy jobs', 'special needs jobs',
  'autism support jobs', 'child development jobs', 'speech therapist jobs', 'occupational therapy jobs',
  'ABA therapist jobs', 'special educator jobs', 'autism diagnosis', 'autism spectrum disorder',
  'ASD jobs', 'autism support', 'child development center jobs', 'autism resources', 'M-CHAT-R'
].join(', ');

// Toast configuration is handled by the ToastContainer component

// Featured jobs data (in a real app, this would come from an API)
const featuredJobs = [
  {
    id: 1,
    title: 'Senior Special Educator',
    company: 'Rainbow Kids Center',
    location: 'Mumbai, Maharashtra',
    type: 'Full-time',
    experience: '3-5 years',
    salary: '₹35,000 - ₹50,000/month',
    posted: '2 days ago',
    urgent: true
  },
  {
    id: 2,
    title: 'Speech Language Pathologist',
    company: 'Little Steps Therapy',
    location: 'Bangalore, Karnataka',
    type: 'Part-time',
    experience: '2-4 years',
    salary: '₹500 - ₹800/session',
    posted: '1 week ago',
    urgent: false
  },
  {
    id: 3,
    title: 'Occupational Therapist',
    company: 'Sunshine Child Development',
    location: 'Delhi NCR',
    type: 'Full-time',
    experience: '1-3 years',
    salary: '₹30,000 - ₹45,000/month',
    posted: '3 days ago',
    urgent: true
  }
];

function Landing() {
  const user = useAuthUser();
  const [referralModal, setReferralModal] = React.useState({ open: false, role: null });
  const navigate = useNavigate();
  
  const [showTerms, setShowTerms] = React.useState(false);
  const [showSignupModal, setShowSignupModal] = React.useState(false);
  const [signupRole, setSignupRole] = React.useState(null);

  const handleOpenSignup = (role) => {
    if (user) {
      // If user is already logged in, navigate to appropriate dashboard
      const userRole = user.role || 'professional';
      navigate(`/${userRole}/dashboard`);
      return;
    }
    
    setSignupRole(role);
    setShowSignupModal(true);
  };

  const handleCloseSignup = () => {
    setShowSignupModal(false);
    setSignupRole(null);
  };
  
  const handleApplyJob = (jobId, e) => {
    e.preventDefault();
    if (!user) {
      toast.info('Please sign in to apply for this job', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      handleOpenSignup('professional');
      return;
    }
    // Check if user has a professional profile
    // This would be replaced with actual profile check
    if (user.role !== 'professional') {
      toast.warn('Please complete your professional profile before applying', {
        position: 'top-center',
        autoClose: 4000,
      });
      navigate('/profile/setup?role=professional');
      return;
    }
    navigate(`/jobs/${jobId}/apply`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>asdflo - Autism Support, Jobs & Resources | Free M-CHAT Test</title>
        <meta name="description" content="Find autism support jobs, take a free M-CHAT test for early autism screening, and access resources for children with autism spectrum disorder." />
        <meta name="keywords" content={seoKeywords} />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="asdflo - Autism Support, Jobs & Resources" />
        <meta property="og:description" content="Find autism support jobs and take a free M-CHAT test for early autism screening." />
        {bgImage && <meta property="og:image" content={`${window.location.origin}${bgImage}`} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="asdflo - Autism Support, Jobs & Resources" />
        <meta name="twitter:description" content="Find autism support jobs and take a free M-CHAT test for early autism screening." />
        {bgImage && <meta name="twitter:image" content={`${window.location.origin}${bgImage}`} />}
      </Helmet>
      
      {/* Toast Notifications */}
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Use the shared Header component */}
      <Header onLoginButtonClick={() => handleOpenSignup(null)} />

      {/* Hero Section */}
      <section className="relative flex flex-col items-center justify-center min-h-[90vh] w-full text-center px-4 py-20 overflow-hidden bg-gray-900">
        {/* Background Video Container */}
        <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover opacity-30"
            aria-label="Background video showing happy children with therapists"
          >
            <source src={`${process.env.PUBLIC_URL}/assets/videos/asdflovideo.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/90 via-primary-800/80 to-primary-900/90 z-0" />
        
        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl w-full px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              Empowering Neurodiverse Growth through <span className="text-primary-200">Inclusive Tech</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl leading-relaxed">
              Connecting parents, therapists & child development centers with the resources they need to thrive.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full max-w-2xl">
              <button
                onClick={() => navigate('/free-assessment')}
                className="btn btn-primary text-lg font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5"
                aria-label="Take the free M-CHAT autism screening test"
              >
                🩺 Take the Free M-CHAT Autism Screening
              </button>
              
              <button
                onClick={() => handleOpenSignup('professional')}
                className="btn btn-outline text-lg font-medium py-4 px-8 rounded-lg border-2 border-white/20 text-white hover:bg-white/10 transition-colors duration-200"
                aria-label="Join as a professional"
              >
                Join as a Professional
              </button>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-12 flex flex-col items-center">
              <div className="flex items-center space-x-2 text-gray-300 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
                <span className="ml-2 text-gray-300">4.9/5 from 200+ reviews</span>
              </div>
              

            </div>
          </div>
        </div>
        
        {/* Animated Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <button
            aria-label="Scroll to features"
            onClick={() => {
              const el = document.getElementById('features');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-transparent border-none animate-bounce focus:outline-none"
            style={{ outline: 'none', border: 'none' }}
          >
            <svg className="w-12 h-12 text-white drop-shadow-lg" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
            <span className="sr-only">Scroll to features</span>
          </button>
        </div>
      </section>
      
      {/* Job Search Section - Only show when user is not logged in */}
      {!user && (
        <section id="job-search" className="py-12 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">Find Opportunities in Special Education</h2>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (!user) {
                    e.preventDefault();
                    handleOpenSignup('professional');
                    return;
                  }
                  const jobTitle = e.target.elements.jobTitle?.value;
                  const location = e.target.elements.location?.value;
                  if (jobTitle) {
                    navigate(`/job-search?q=${encodeURIComponent(jobTitle)}${location ? `&loc=${encodeURIComponent(location)}` : ''}`);
                  }
                }} className="space-y-4">
                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="jobTitle"
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        placeholder="Job title, keywords, or company"
                        required
                      />
                    </div>
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaMapMarkerAlt className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="location"
                        className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                        placeholder="City, state, or zip code"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 flex items-center justify-center"
                    >
                      <FaSearch className="mr-2" />
                      Search Jobs
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 text-center">
                    <span className="mr-2">Popular searches:</span>
                    {['Special Educator', 'Speech Therapist', 'Occupational Therapist', 'Behavior Therapist'].map((term, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => navigate(`/job-search?q=${encodeURIComponent(term)}`)}
                        className="text-blue-600 hover:text-blue-800 hover:underline mr-3"
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Featured Jobs Section - Only show when user is not logged in */}
      {!user && (
        <section id="featured-jobs" className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Featured Job Opportunities
              </h2>
              <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
                Browse our latest job postings from top employers in special education
              </p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {featuredJobs.map((job) => (
                <div key={job.id} className={`bg-white overflow-hidden shadow rounded-lg border-l-4 ${job.urgent ? 'border-red-500' : 'border-blue-500'}`}>
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">{job.title}</h3>
                      {job.urgent && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Urgent
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{job.company}</p>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <FaMapMarkerAlt className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                      {job.location}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {job.type}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {job.experience} exp
                      </span>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{job.salary}</span>
                      <span className="text-xs text-gray-500">{job.posted}</span>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={(e) => handleApplyJob(job.id, e)}
                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                      >
                        {user ? 'Apply Now' : 'Sign In to Apply'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <button
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault();
                    handleOpenSignup('professional');
                  } else {
                    navigate('/job-search');
                  }
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
              >
                View All Jobs <FaArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        </section>
      )}

      <ReferralModal open={referralModal.open} onClose={() => setReferralModal({ open: false, role: null })} defaultRole={referralModal.role} />

      {/* Features Section */}
      <PlatformFeatures onOpenSignup={handleOpenSignup} />

      {/* Video Section */}
      <section className="w-full py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Experience the asdflo Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See how our platform transforms lives through personalized care and support
            </p>
          </div>
          <div className="max-w-4xl mx-auto rounded-xl overflow-hidden shadow-2xl bg-black">
            <div className="relative group cursor-pointer" onClick={(e) => {
              const video = e.currentTarget.querySelector('video');
              video.controls = true;
              video.play();
              e.currentTarget.querySelector('.play-overlay').classList.add('hidden');
            }}>
              <video
                id="asdflo-video"
                playsInline
                preload="metadata"
                className="w-full h-auto max-h-[80vh]"
                aria-label="asdflo platform demonstration video"
              >
                <source src={`${process.env.PUBLIC_URL}/assets/videos/asdflovideo.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="play-overlay absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity duration-300 group-hover:bg-black/40">
                <div className="bg-white/90 hover:bg-white transition-all duration-300 rounded-full p-4 group-hover:scale-110">
                  <svg className="w-12 h-12 md:w-16 md:h-16 text-indigo-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Blog Section */}
      <BlogSection />
  
      {/* Signup Modal */}
      <SignupModal show={showSignupModal} onClose={handleCloseSignup} initialRole={signupRole} />

      {/* Footer */}
      <Footer />

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/918978733122" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 w-14 h-14 sm:w-16 sm:h-16 bg-green-500 text-white rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 transition-all z-30" 
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp className="w-8 h-8" />
      </a>
    </div>
  );
}

export default Landing;